import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { setAuth } from '../Components/Redux/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaMapMarkerAlt, FaUserGraduate, FaCheck, FaTimes } from 'react-icons/fa';

interface Class {
  id: number;
  name: string;
}

interface Section {
  id: number;
  name: string;
  class_associated: number;
}

interface Student {
  id: number;
  name: string;
  class_associated: number;
  section_associated: number;
}

interface Parent {
  user: number;
  name: string;
  phone_number: string;
  address: string;
  students: number[];
}

interface UserRegistration {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirm: string;
  role: string;
  branch?: number;
  branch_name?: string;
}

const RegisterAndCreateParent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const [step, setStep] = useState<number>(1);
  const [userData, setUserData] = useState<UserRegistration>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: '',
    role: 'parent',
  });

  const [parentData, setParentData] = useState<Parent>({
    user: 0,
    name: '',
    phone_number: '',
    address: '',
    students: [],
  });

  const [classes, setClasses] = useState<Class[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordMatch, setPasswordMatch] = useState<boolean>(true);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/users/user');
        console.log(response.data.user.role);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching user data:', error);
        dispatch(setAuth(false));
        setError('Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [dispatch]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get<any>('/Branches/classes/');
        setClasses(response.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
        setError('Failed to fetch classes.');
      }
    };

    fetchClasses();
  }, []);

  useEffect(() => {
    const fetchSectionsByClass = async () => {
      if (selectedClass) {
        try {
          const response = await axios.get<any>(
            `/Branches/sections/?class_id=${selectedClass}`
          );
          setSections(response.data);
        } catch (error) {
          console.error('Error fetching sections:', error);
          setError('Failed to fetch sections.');
        }
      } else {
        setSections([]);
      }
    };

    fetchSectionsByClass();
  }, [selectedClass]);

  useEffect(() => {
    const fetchStudentsBySection = async () => {
      if (selectedSection) {
        try {
          const response = await axios.get(
            `/Branches/students/?section_id=${selectedSection}`
          );
          const data = response.data;
          const studentsData = data.results ? data.results : (Array.isArray(data) ? data : [data]);
          setStudents(studentsData);
        } catch (error: any) {
          console.error('Error fetching students:', error);
          setError('Failed to fetch students.');
        }
      } else {
        setStudents([]);
      }
    };

    fetchStudentsBySection();
  }, [selectedSection]);

  // Check password match
  useEffect(() => {
    if (userData.password_confirm) {
      setPasswordMatch(userData.password === userData.password_confirm);
    }
  }, [userData.password, userData.password_confirm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordMatch) {
      setError('Lambarka sirta isma lahan!');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Step 1: Register the user
      const userResponse = await axios.post('/users/register', userData);

      // Step 2: Create the parent
      const parentPayload = {
        ...parentData,
        user: userResponse.data.id,
        students: parentData.students,
      };

      await axios.post('/Branches/parents/', parentPayload);

      setSuccess('Waalidka si guul leh ayaa loo diiwaangeliyay!');
      setTimeout(() => {
        navigate('/parents');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Khalad ayaa dhacay markii la sameynayay waalidka.');
      console.error('Error:', err.response ? err.response.data : err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });

    // Auto-fill parent name from user names
    if (name === 'first_name' || name === 'last_name') {
      const firstName = name === 'first_name' ? value : userData.first_name;
      const lastName = name === 'last_name' ? value : userData.last_name;
      setParentData({ ...parentData, name: `${firstName} ${lastName}`.trim() });
    }
  };

  const handleParentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setParentData({ ...parentData, [name]: value });
  };

  const toggleStudentSelection = (studentId: number) => {
    const currentStudents = [...parentData.students];
    const index = currentStudents.indexOf(studentId);

    if (index > -1) {
      currentStudents.splice(index, 1);
    } else {
      currentStudents.push(studentId);
    }

    setParentData({ ...parentData, students: currentStudents });
  };

  const nextStep = () => {
    if (step === 1 && (!userData.first_name || !userData.last_name || !userData.email || !userData.password || !passwordMatch)) {
      setError('Fadlan buuxi dhammaan faahfaahinta ciwaanka si sax ah');
      return;
    }
    if (step === 2 && (!parentData.phone_number || !parentData.address)) {
      setError('Fadlan buuxi faahfaahinta waalidka');
      return;
    }
    setError(null);
    setStep(step + 1);
  };

  const prevStep = () => {
    setError(null);
    setStep(step - 1);
  };

  if (!auth) {
    return (
      <div className="text-center mt-5">
        <h4 className="text-muted">Ma aadan xaqiijin</h4>
        <a href="/login" className="btn btn-primary mt-3">
          Aad Login
        </a>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="container-fluid px-4">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-8">
            {/* Header */}
            <div className="text-center mb-5">
              <h1 className="h2 fw-bold text-dark mb-2">👨‍👩‍👧‍👦 Diiwaangeli Waalid Cusub</h1>
              <p className="text-muted">Samee ciwaanka waalidka oo ku xir ardayda 3 tallaabo oo fudud</p>
            </div>

            {/* Progress Steps */}
            <div className="row mb-5">
              <div className="col-12">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <div className={`d-flex align-items-center ${step >= 1 ? 'text-primary' : 'text-muted'}`}>
                      <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 1 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '40px', height: '40px' }}>
                        {step > 1 ? <FaCheck /> : '1'}
                      </div>
                      <span className="ms-2 fw-semibold d-none d-md-inline">Macluumaadka Ciwaanka</span>
                    </div>
                  </div>
                  <div className={`flex-grow-1 border-top ${step >= 2 ? 'border-primary' : 'border-secondary'}`} style={{ height: '2px', marginBottom: '20px' }}></div>
                  <div className="flex-grow-1">
                    <div className={`d-flex align-items-center ${step >= 2 ? 'text-primary' : 'text-muted'}`}>
                      <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 2 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '40px', height: '40px' }}>
                        {step > 2 ? <FaCheck /> : '2'}
                      </div>
                      <span className="ms-2 fw-semibold d-none d-md-inline">Faahfaahinta Xiriirka</span>
                    </div>
                  </div>
                  <div className={`flex-grow-1 border-top ${step >= 3 ? 'border-primary' : 'border-secondary'}`} style={{ height: '2px', marginBottom: '20px' }}></div>
                  <div className="flex-grow-1">
                    <div className={`d-flex align-items-center ${step >= 3 ? 'text-primary' : 'text-muted'}`}>
                      <div className={`rounded-circle d-flex align-items-center justify-content-center ${step >= 3 ? 'bg-primary text-white' : 'bg-secondary text-white'}`} style={{ width: '40px', height: '40px' }}>
                        3
                      </div>
                      <span className="ms-2 fw-semibold d-none d-md-inline">Ku Xir Ardayda</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Alerts */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show border-0 shadow-sm mb-4" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
              </div>
            )}
            {success && (
              <div className="alert alert-success alert-dismissible fade show border-0 shadow-sm mb-4" role="alert">
                <i className="bi bi-check-circle-fill me-2"></i>
                {success}
                <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
              </div>
            )}

            {/* Form Card */}
            <div className="card border-0 shadow-lg">
              <div className="card-body p-5">
                <form onSubmit={handleSubmit}>
                  {/* Step 1: Account Information */}
                  {step === 1 && (
                    <div>
                      <h5 className="fw-bold mb-4 text-primary">
                        <FaUser className="me-2" />
                        Macluumaadka Ciwaanka
                      </h5>
                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">Magaca Koowaad *</label>
                          <input
                            type="text"
                            name="first_name"
                            className="form-control form-control-lg border-2"
                            value={userData.first_name}
                            onChange={handleUserInputChange}
                            placeholder="Geli magaca koowaad"
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">Magaca Labaad *</label>
                          <input
                            type="text"
                            name="last_name"
                            className="form-control form-control-lg border-2"
                            value={userData.last_name}
                            onChange={handleUserInputChange}
                            placeholder="Geli magaca labaad"
                            required
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-semibold text-secondary small">
                            <FaEnvelope className="me-2" />
                            Ciwaanka Email-ka *
                          </label>
                          <input
                            type="email"
                            name="email"
                            className="form-control form-control-lg border-2"
                            value={userData.email}
                            onChange={handleUserInputChange}
                            placeholder="waalid@example.com"
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">
                            <FaLock className="me-2" />
                            Lambarka Sirta *
                          </label>
                          <input
                            type="password"
                            name="password"
                            className="form-control form-control-lg border-2"
                            value={userData.password}
                            onChange={handleUserInputChange}
                            placeholder="Abuur lambar sir ah oo adag"
                            required
                          />
                          <small className="text-muted">Ugu yaraan 8 xarfood</small>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">
                            <FaLock className="me-2" />
                            Xaqiiji Lambarka Sirta *
                          </label>
                          <input
                            type="password"
                            name="password_confirm"
                            className={`form-control form-control-lg border-2 ${userData.password_confirm && (passwordMatch ? 'is-valid' : 'is-invalid')}`}
                            value={userData.password_confirm}
                            onChange={handleUserInputChange}
                            placeholder="Geli markale lambarka sirta"
                            required
                          />
                          {userData.password_confirm && (
                            <div className={passwordMatch ? 'valid-feedback' : 'invalid-feedback'}>
                              {passwordMatch ? (
                                <><FaCheck className="me-1" /> Waa isku mid</>
                              ) : (
                                <><FaTimes className="me-1" /> Iskumid ma aha</>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Parent/Contact Details */}
                  {step === 2 && (
                    <div>
                      <h5 className="fw-bold mb-4 text-primary">
                        <FaPhone className="me-2" />
                        Faahfaahinta Xiriirka
                      </h5>
                      <div className="row g-4">
                        <div className="col-12">
                          <label className="form-label fw-semibold text-secondary small">Magaca Oo Dhan *</label>
                          <input
                            type="text"
                            name="name"
                            className="form-control form-control-lg border-2"
                            value={parentData.name}
                            onChange={handleParentInputChange}
                            placeholder="Magaca waalidka/masuulka"
                            required
                          />
                          <small className="text-muted">Si toos ah u qaatay magacyada ciwaanka</small>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">
                            <FaPhone className="me-2" />
                            Lambarka Telefoonka *
                          </label>
                          <input
                            type="tel"
                            name="phone_number"
                            className="form-control form-control-lg border-2"
                            value={parentData.phone_number}
                            onChange={handleParentInputChange}
                            placeholder="061XXXXXXX"
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">
                            <FaMapMarkerAlt className="me-2" />
                            Cinwaanka *
                          </label>
                          <input
                            type="text"
                            name="address"
                            className="form-control form-control-lg border-2"
                            value={parentData.address}
                            onChange={handleParentInputChange}
                            placeholder="Cinwaanka guriga"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Link Students */}
                  {step === 3 && (
                    <div>
                      <h5 className="fw-bold mb-4 text-primary">
                        <FaUserGraduate className="me-2" />
                        Ku Xir Ardayda
                      </h5>
                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">Xulo Fasalka *</label>
                          <select
                            className="form-select form-select-lg border-2"
                            value={selectedClass || ''}
                            onChange={(e) => {
                              setSelectedClass(Number(e.target.value));
                              setSelectedSection(null);
                              setParentData({ ...parentData, students: [] });
                            }}
                            required
                          >
                            <option value="">Dooro fasal</option>
                            {classes.map((cls) => (
                              <option key={cls.id} value={cls.id}>
                                {cls.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-secondary small">Xulo Qeybta *</label>
                          <select
                            className="form-select form-select-lg border-2"
                            value={selectedSection || ''}
                            onChange={(e) => {
                              setSelectedSection(Number(e.target.value));
                              setParentData({ ...parentData, students: [] });
                            }}
                            disabled={!selectedClass}
                            required
                          >
                            <option value="">Dooro qeyb</option>
                            {sections.map((section) => (
                              <option key={section.id} value={section.id}>
                                {section.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-semibold text-secondary small">
                            Xulo Ardayda * ({parentData.students.length} la doortay)
                          </label>
                          {students.length > 0 ? (
                            <div className="row g-3">
                              {students.map((student) => (
                                <div key={student.id} className="col-md-6">
                                  <div
                                    className={`card border-2 cursor-pointer ${parentData.students.includes(student.id)
                                      ? 'border-primary bg-primary bg-opacity-10'
                                      : 'border-secondary'
                                      }`}
                                    onClick={() => toggleStudentSelection(student.id)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div className="card-body p-3">
                                      <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                          <FaUserGraduate className={parentData.students.includes(student.id) ? 'text-primary' : 'text-secondary'} />
                                          <span className="fw-semibold">{student.name}</span>
                                        </div>
                                        {parentData.students.includes(student.id) && (
                                          <FaCheck className="text-primary" />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="alert alert-info">
                              <i className="bi bi-info-circle me-2"></i>
                              {selectedSection ? 'Arday lagama helin qeybtan' : 'Fadlan dooro fasal iyo qeyb marka hore'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="d-flex justify-content-between mt-5 pt-4 border-top">
                    {step > 1 && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-lg rounded-pill px-5"
                        onClick={prevStep}
                      >
                        Hore
                      </button>
                    )}
                    {step < 3 ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-lg rounded-pill px-5 ms-auto"
                        onClick={nextStep}
                      >
                        Tallaabada Xigta
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-success btn-lg rounded-pill px-5 ms-auto shadow"
                        disabled={loading || parentData.students.length === 0}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Samaynaya...
                          </>
                        ) : (
                          <>
                            <FaCheck className="me-2" />
                            Samee Waalidka
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterAndCreateParent;